import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Badge,
    Tabs,
    Tab,
    Alert,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import termService from '@/shared/services/termService';
import ProgramWindowList from '@/components/common/ProgramWindowList';
import ProgramComingSoon from '@/components/common/ProgramComingSoon';
import { StudentApplicationsNotOpen } from '@/components/common/ProgramPageLinks';
import useProgramAvailability from '@/shared/hooks/useProgramAvailability';
import '@/features/home/HomePage.css';

function HomePage() {
    const { currentUser, userProfile, effectiveRoles } = useAuth();
    const roles = resolveRoles({ effectiveRoles, userProfile });
    // The Piper and Rowan cards describe assistants with no AI behind them
    // yet (#391), so they go with the assistants flag, not just the widget.
    const assistantsEnabled = useFeature('assistants');
    // Every invitation to students to apply for a position goes with the
    // flag that owns that workflow.
    const studentApplicationEnabled = useFeature('studentApplication');
    // Read signed out as well as in, so the program cards cannot wait for a
    // term: the public switch per program is what marks one "Coming soon".
    const programActive = useProgramAvailability();
    const comingSoon = (programType) => programActive?.[programType] === false;
    // Same audience as the Request Support route and its menu entry.
    const canRequestSupport = roles.some((role) =>
        ['faculty', 'admin'].includes(role)
    );
    const [activeTerm, setActiveTerm] = useState(null);

    useEffect(() => {
        termService
            .getCurrentTerm()
            .then(setActiveTerm)
            .catch(() => {});
    }, []);

    return (
        <div className="home-page">
            {/* Hero Section */}
            <section className="bg-primary text-white py-5">
                <Container>
                    <Row className="align-items-center py-4">
                        <Col lg={7}>
                            <h1 className="display-4 fw-bold mb-3">
                                Peer Support Hub
                            </h1>
                            <p className="lead mb-4">
                                MSU Denver&apos;s unified platform for
                                peer-based instructional support. Faculty can
                                request peer support for their courses.
                                {studentApplicationEnabled &&
                                    ' Students can apply to become peer supporters.'}
                            </p>
                            <p className="mb-4">
                                One platform. Three programs. Coordinated
                                support for student success.
                            </p>
                            {!currentUser && (
                                <div className="d-flex gap-3 flex-wrap">
                                    <Button
                                        as={Link}
                                        to="/signup"
                                        variant="light"
                                        size="lg"
                                    >
                                        Get Started
                                    </Button>
                                    <Button
                                        as={Link}
                                        to="/about"
                                        variant="outline-light"
                                        size="lg"
                                    >
                                        Learn More
                                    </Button>
                                </div>
                            )}
                            {currentUser && (
                                <div className="d-flex gap-3 flex-wrap">
                                    {canRequestSupport && (
                                        <Button
                                            as={Link}
                                            to="/applications/start"
                                            variant="light"
                                            size="lg"
                                        >
                                            Request Support
                                        </Button>
                                    )}
                                    <Button
                                        as={Link}
                                        to="/dashboard"
                                        variant={
                                            canRequestSupport
                                                ? 'outline-light'
                                                : 'light'
                                        }
                                        size="lg"
                                    >
                                        Go to Dashboard
                                    </Button>
                                    <Button
                                        as={Link}
                                        to="/courses"
                                        variant="outline-light"
                                        size="lg"
                                    >
                                        Browse Courses
                                    </Button>
                                </div>
                            )}
                        </Col>
                        <Col lg={5} className="d-none d-lg-block text-center">
                            <div className="p-4">
                                <div className="d-flex justify-content-center gap-3 mb-3">
                                    <Badge
                                        bg=""
                                        className="fs-5 px-3 py-2 badge-la-custom"
                                    >
                                        LA
                                    </Badge>
                                    <Badge
                                        bg="success"
                                        className="fs-5 px-3 py-2"
                                    >
                                        SI
                                    </Badge>
                                    <Badge
                                        bg="light"
                                        text="primary"
                                        className="fs-5 px-3 py-2 border border-white"
                                    >
                                        TA
                                    </Badge>
                                </div>
                                <p className="text-white small">
                                    Three programs, unified coordination
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Application Timeline Banner: each program's window for the
                signed-in viewer, as the server decides it */}
            {activeTerm?.applicationWindows && (
                <section
                    className="py-3 bg-white border-bottom"
                    aria-labelledby="home-application-windows-heading"
                >
                    <Container>
                        <Row className="justify-content-center">
                            <Col md={10} lg={8}>
                                <h2
                                    id="home-application-windows-heading"
                                    className="h6 fw-bold text-center mb-2"
                                >
                                    {activeTerm.name} application windows
                                </h2>
                                <ProgramWindowList
                                    windows={activeTerm.applicationWindows}
                                    className="d-flex flex-column align-items-center"
                                />
                            </Col>
                        </Row>
                    </Container>
                </section>
            )}

            {/* Programs Section */}
            <section className="py-5">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="fw-bold">Our Programs</h2>
                        <p className="text-muted lead">
                            Three distinct pathways to support student success
                        </p>
                    </div>
                    <Row>
                        <Col lg={4} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm program-card">
                                <div className="card-header la-header text-white py-3">
                                    <h3 className="h4 mb-0">
                                        <Badge
                                            bg="light"
                                            text="dark"
                                            className="me-2"
                                        >
                                            LA
                                        </Badge>
                                        Learning Assistants
                                    </h3>
                                </div>
                                <Card.Body>
                                    {comingSoon('la') && (
                                        <ProgramComingSoon
                                            programType="la"
                                            className="mb-3"
                                        />
                                    )}
                                    <p>
                                        LAs are undergraduate students,
                                        primarily in STEM courses, selected for
                                        their interest in teaching and their
                                        ability to support student learning in
                                        interactive classroom environments. The
                                        main role of an LA is to help students
                                        learn and to help students learn how to
                                        learn.
                                    </p>
                                    <h4 className="h6 fw-bold mt-4">
                                        What LAs Do:
                                    </h4>
                                    <ul className="small">
                                        <li>
                                            Facilitate small group discussions
                                            in class
                                        </li>
                                        <li>
                                            Promote problem-solving strategies
                                        </li>
                                        <li>
                                            Facilitate collaborative learning
                                        </li>
                                        <li>
                                            Acquire pedagogical training skills
                                            that transfer to workforce readiness
                                        </li>
                                    </ul>
                                    <h4 className="h6 fw-bold mt-3">
                                        Requirements:
                                    </h4>
                                    <ul className="small text-muted">
                                        <li>
                                            Must have previously completed and
                                            passed the course they will support
                                            or demonstrate equivalent content
                                            knowledge.
                                        </li>
                                        <li>
                                            Have the time and commitment to
                                            complete the LA pedagogy trainings
                                        </li>
                                        <li>
                                            Strong interpersonal and
                                            communication skills
                                        </li>
                                        <li>
                                            Commitment to working full semester
                                            and to attend the assigned class
                                        </li>
                                    </ul>
                                </Card.Body>
                                <Card.Footer className="bg-transparent border-0 pb-3">
                                    <Button
                                        as={Link}
                                        to="/about#la"
                                        variant="outline-la"
                                        className="w-100"
                                    >
                                        Learn More About LA
                                    </Button>
                                </Card.Footer>
                            </Card>
                        </Col>
                        <Col lg={4} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm program-card">
                                <div className="card-header bg-success text-white py-3">
                                    <h3 className="h4 mb-0">
                                        <Badge
                                            bg="light"
                                            text="success"
                                            className="me-2"
                                        >
                                            SI
                                        </Badge>
                                        Supplemental Instruction
                                    </h3>
                                </div>
                                <Card.Body>
                                    {comingSoon('si') && (
                                        <ProgramComingSoon
                                            programType="si"
                                            className="mb-3"
                                        />
                                    )}
                                    <p>
                                        SI targets historically difficult
                                        courses, not at-risk students. SI
                                        Leaders attend class alongside students
                                        and facilitate collaborative study
                                        sessions outside of class.
                                    </p>
                                    <h4 className="h6 fw-bold mt-4">
                                        What SI Leaders Do:
                                    </h4>
                                    <ul className="small">
                                        <li>
                                            Attend all class sessions for their
                                            assigned course
                                        </li>
                                        <li>
                                            Plan and lead 2-3 study sessions per
                                            week
                                        </li>
                                        <li>
                                            Model effective study strategies
                                        </li>
                                        <li>
                                            Create collaborative learning
                                            environments
                                        </li>
                                    </ul>
                                    <h4 className="h6 fw-bold mt-3">
                                        Requirements:
                                    </h4>
                                    <ul className="small text-muted">
                                        <li>
                                            Previously earned A or B in the
                                            course
                                        </li>
                                        <li>
                                            Strong academic standing (2.5+ GPA)
                                        </li>
                                        <li>Complete SI Leader training</li>
                                    </ul>
                                </Card.Body>
                                <Card.Footer className="bg-transparent border-0 pb-3">
                                    <Button
                                        as={Link}
                                        to="/about#si"
                                        variant="outline-success"
                                        className="w-100"
                                    >
                                        Learn More About SI
                                    </Button>
                                </Card.Footer>
                            </Card>
                        </Col>
                        <Col lg={4} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm program-card">
                                <div className="card-header bg-primary text-white py-3">
                                    <h3 className="h4 mb-0">
                                        <Badge
                                            bg="light"
                                            text="primary"
                                            className="me-2"
                                        >
                                            TA
                                        </Badge>
                                        Teaching Assistants
                                    </h3>
                                </div>
                                <Card.Body>
                                    {comingSoon('ta') && (
                                        <ProgramComingSoon
                                            programType="ta"
                                            className="mb-3"
                                        />
                                    )}
                                    <p>
                                        TAs provide direct instructional support
                                        to faculty members. Faculty identify
                                        specific students they want to work
                                        with, and the TA program coordinates the
                                        employment process.
                                    </p>
                                    <h4 className="h6 fw-bold mt-4">
                                        What TAs Do:
                                    </h4>
                                    <ul className="small">
                                        <li>
                                            Assist with lab sessions and
                                            recitations
                                        </li>
                                        <li>
                                            Hold office hours for student
                                            questions
                                        </li>
                                        <li>Help with grading and feedback</li>
                                        <li>Support classroom activities</li>
                                    </ul>
                                    <h4 className="h6 fw-bold mt-3">
                                        How It Works:
                                    </h4>
                                    <ul className="small text-muted">
                                        <li>
                                            Faculty request specific students as
                                            TAs
                                        </li>
                                        <li>
                                            Named student must accept the
                                            position
                                        </li>
                                        <li>
                                            TA Coordinator processes the
                                            assignment
                                        </li>
                                    </ul>
                                </Card.Body>
                                <Card.Footer className="bg-transparent border-0 pb-3">
                                    <Button
                                        as={Link}
                                        to="/about#ta"
                                        variant="outline-primary"
                                        className="w-100"
                                    >
                                        Learn More About TA
                                    </Button>
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* How It Works Section - Dual Audience */}
            <section className="bg-light py-5">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="fw-bold">How It Works</h2>
                        <p className="text-muted lead">
                            A streamlined process for both faculty and students
                        </p>
                    </div>
                    <Row className="justify-content-center">
                        <Col md={10} lg={8}>
                            <Tabs
                                defaultActiveKey="faculty"
                                className="mb-4"
                                justify
                            >
                                <Tab eventKey="faculty" title="Faculty & Staff">
                                    <div className="pt-3">
                                        <Alert variant="info" className="small">
                                            Faculty and staff submit course
                                            support applications to request peer
                                            support for their courses.
                                        </Alert>
                                        <StepItem
                                            number={1}
                                            title="Sign In"
                                            description="Log in with your MSU Denver account. First-time users confirm their department and college affiliation."
                                        />
                                        <StepItem
                                            number={2}
                                            title="Select Your Course"
                                            description="Choose the course section that needs peer support. Course data is imported from the university system."
                                        />
                                        <StepItem
                                            number={3}
                                            title="Rank Program Preferences"
                                            description="Select the programs you want and rank them in order of preference. A program not accepting applications this term is marked Coming soon."
                                        />
                                        <StepItem
                                            number={4}
                                            title="Programs Review Independently"
                                            description="Each program reviews your application on their own timeline. Lower-ranked programs may issue contingent awards while waiting on higher-ranked decisions."
                                        />
                                        <StepItem
                                            number={5}
                                            title="Receive Decision & Coordinate"
                                            description="Get notified of each program's decision. Once approved, coordinate with your assigned peer supporter on schedule and expectations."
                                            isLast
                                        />
                                    </div>
                                </Tab>
                                <Tab eventKey="students" title="Students">
                                    {!studentApplicationEnabled && (
                                        <StudentApplicationsNotOpen className="pt-3" />
                                    )}
                                    {studentApplicationEnabled && (
                                        <div className="pt-3">
                                            <Alert
                                                variant="info"
                                                className="small"
                                            >
                                                Students apply to become
                                                Learning Assistants, SI Leaders,
                                                or TAs to support courses
                                                they&apos;ve excelled in.
                                            </Alert>
                                            <StepItem
                                                number={1}
                                                title="Create Your Account"
                                                description="Sign up with your MSU Denver email to get started. Complete your student profile with your academic information."
                                            />
                                            <StepItem
                                                number={2}
                                                title="Browse Available Courses"
                                                description="Explore courses seeking peer support. Filter by department, course level, or program type to find the right fit."
                                            />
                                            <StepItem
                                                number={3}
                                                title="Submit Your Application"
                                                description="Apply to support a course and rank your program preferences. Include your qualifications, availability, and experience."
                                            />
                                            <StepItem
                                                number={4}
                                                title="Programs Review Your Application"
                                                description="Each program reviews applications based on their timeline. Your preferences are honored: higher-ranked programs get first consideration."
                                            />
                                            <StepItem
                                                number={5}
                                                title="Get Assigned & Start Training"
                                                description="Once approved, complete your program-specific training and begin supporting students in your assigned course."
                                                isLast
                                            />
                                        </div>
                                    )}
                                </Tab>
                            </Tabs>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Benefits Section */}
            <section className="py-5">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="fw-bold">Why Peer Support Matters</h2>
                        <p className="text-muted lead">
                            Benefits for students, faculty, and the university
                        </p>
                    </div>
                    <Row>
                        <Col md={6} lg={3} className="mb-4">
                            <Card className="h-100 border-0 text-center p-3">
                                <Card.Body>
                                    <div className="display-4 mb-3">📈</div>
                                    <h3 className="h5 fw-bold">
                                        Improved Outcomes
                                    </h3>
                                    <p className="text-muted small mb-0">
                                        Peer-supported courses see higher pass
                                        rates, lower DFW rates, and better
                                        student engagement.
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6} lg={3} className="mb-4">
                            <Card className="h-100 border-0 text-center p-3">
                                <Card.Body>
                                    <div className="display-4 mb-3">💼</div>
                                    <h3 className="h5 fw-bold">
                                        Paid Experience
                                    </h3>
                                    <p className="text-muted small mb-0">
                                        Peer supporters earn competitive wages
                                        while gaining leadership and teaching
                                        skills for their careers.
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6} lg={3} className="mb-4">
                            <Card className="h-100 border-0 text-center p-3">
                                <Card.Body>
                                    <div className="display-4 mb-3">🤝</div>
                                    <h3 className="h5 fw-bold">
                                        Faculty Support
                                    </h3>
                                    <p className="text-muted small mb-0">
                                        Faculty get dedicated support for
                                        challenging courses, freeing time to
                                        focus on instruction and research.
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6} lg={3} className="mb-4">
                            <Card className="h-100 border-0 text-center p-3">
                                <Card.Body>
                                    <div className="display-4 mb-3">🎯</div>
                                    <h3 className="h5 fw-bold">
                                        Equitable Access
                                    </h3>
                                    <p className="text-muted small mb-0">
                                        Unified coordination ensures peer
                                        support is distributed fairly across
                                        departments and courses.
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* For Faculty Section */}
            <section className="bg-light py-5">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} className="mb-4 mb-lg-0">
                            <h2 className="fw-bold">For Faculty</h2>
                            <p className="lead text-muted">
                                Get the support your courses need
                            </p>
                            <p>
                                The Peer Support Hub makes it easy to request
                                and manage peer support for your courses. Submit
                                one application, rank your program preferences,
                                and each program reviews independently on their
                                own timeline.
                            </p>
                            <ul>
                                <li>
                                    Request peer support with one application
                                    per course section
                                </li>
                                <li>Name a student you already have in mind</li>
                                <li>
                                    Track application status across all programs
                                </li>
                                <li>
                                    Coordinate with assigned peer supporters
                                </li>
                            </ul>
                            {!currentUser && (
                                <Button
                                    as={Link}
                                    to="/signup"
                                    variant="primary"
                                >
                                    Create Faculty Account
                                </Button>
                            )}
                        </Col>
                        {assistantsEnabled && (
                            <Col lg={6}>
                                <Card className="border-0 shadow">
                                    <Card.Body className="p-4">
                                        <h3 className="h5 fw-bold mb-3">
                                            Need Help?
                                        </h3>
                                        <p className="text-muted">
                                            Our AI assistant{' '}
                                            <strong>Piper</strong> can help you
                                            navigate the peer support request
                                            process, answer questions about
                                            program requirements, and guide you
                                            through the system.
                                        </p>
                                        <div className="bg-light rounded p-3 text-center">
                                            <Badge
                                                bg="secondary"
                                                className="mb-2"
                                            >
                                                Coming Soon
                                            </Badge>
                                            <p className="small text-muted mb-0">
                                                Piper - Your Faculty Support
                                                Assistant
                                            </p>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </Container>
            </section>

            {/* For Students Section */}
            <section className="py-5">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} className="order-lg-2 mb-4 mb-lg-0">
                            <h2 className="fw-bold">For Students</h2>
                            <p className="lead text-muted">
                                Turn your success into opportunity
                            </p>
                            <p>
                                Did you excel in a course? Help other students
                                succeed while earning money and building your
                                resume.
                                {studentApplicationEnabled &&
                                    " The Peer Support Hub lets you apply to support courses you've mastered and choose which program fits your interests."}
                            </p>
                            {studentApplicationEnabled ? (
                                <>
                                    <ul>
                                        <li>One simple application process</li>
                                        <li>Rank your program preferences</li>
                                        <li>Track your application status</li>
                                        <li>
                                            Manage your assignments in one place
                                        </li>
                                    </ul>
                                    {!currentUser && (
                                        <Button
                                            as={Link}
                                            to="/signup"
                                            variant="primary"
                                        >
                                            Start Your Application
                                        </Button>
                                    )}
                                </>
                            ) : (
                                <StudentApplicationsNotOpen />
                            )}
                        </Col>
                        {assistantsEnabled && (
                            <Col lg={6} className="order-lg-1">
                                <Card className="border-0 shadow">
                                    <Card.Body className="p-4">
                                        <h3 className="h5 fw-bold mb-3">
                                            Questions?
                                        </h3>
                                        <p className="text-muted">
                                            Our AI assistant{' '}
                                            <strong>Rowan</strong> can help you
                                            understand program requirements,
                                            guide you through the application
                                            process, and answer questions about
                                            being a peer supporter.
                                        </p>
                                        <div className="bg-light rounded p-3 text-center">
                                            <Badge
                                                bg="secondary"
                                                className="mb-2"
                                            >
                                                Coming Soon
                                            </Badge>
                                            <p className="small text-muted mb-0">
                                                Rowan - Your Student Support
                                                Assistant
                                            </p>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </Container>
            </section>

            {/* CTA Section */}
            <section className="bg-primary text-white py-5">
                <Container className="text-center">
                    <h2 className="fw-bold mb-3">Ready to Get Involved?</h2>
                    <p className="lead mb-4">
                        {studentApplicationEnabled
                            ? "Whether you're faculty seeking course support or a student looking to lead, the Peer Support Hub connects you with the right program."
                            : 'Faculty seeking peer support for a course can request it through the Peer Support Hub.'}
                    </p>
                    {!currentUser ? (
                        <div className="d-flex justify-content-center gap-3 flex-wrap">
                            <Button
                                as={Link}
                                to="/signup"
                                variant="light"
                                size="lg"
                            >
                                Create Account
                            </Button>
                            <Button
                                as={Link}
                                to="/login"
                                variant="outline-light"
                                size="lg"
                            >
                                Sign In
                            </Button>
                        </div>
                    ) : (
                        <Button
                            as={Link}
                            to="/courses"
                            variant="light"
                            size="lg"
                        >
                            Browse Available Courses
                        </Button>
                    )}
                </Container>
            </section>
        </div>
    );
}

function StepItem({ number, title, description, isLast }) {
    return (
        <Row className={`${isLast ? '' : 'mb-4'} align-items-center`}>
            <Col xs={2} className="text-center">
                <div
                    className={`rounded-circle ${isLast ? 'bg-success' : 'bg-primary'} text-white d-inline-flex align-items-center justify-content-center fw-bold step-circle`}
                >
                    {number}
                </div>
            </Col>
            <Col xs={10}>
                <h3 className="h5 fw-bold mb-1">{title}</h3>
                <p className="text-muted mb-0">{description}</p>
            </Col>
        </Row>
    );
}

export default HomePage;
